import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "policy-check-suggestions",
  courseId: "policies",
  order: 2,
  title: "Policy checks",
  subtitle: "Reports checked against policy",
  summary:
    "Every report is compared against the section of your department's policy that applies to the incident. The draft cites it, leaves a placeholder where policy needs a detail you did not give, and the policy check posts what is still missing as a suggestion.",
  learn: [
    "Read the inline policy citations and policy placeholders in a draft",
    "Recognize a policy check suggestion and open the section it cites",
    "Fill the gap and re-evaluate to clear it",
    "Know when none of this appears",
  ],
  minutes: 4,
  releasedAt: "2026-10-08T00:00:00.000Z",
  gate: "agency",
  sceneId: "policy-check-suggestions",
  tourId: "policy-check-suggestions",
  body: [
    {
      type: "paragraph",
      text: "A use of force report that leaves out the supervisor notification is the kind of report that comes back. When your department has uploaded its policies, every report is compared against the specific section that applies to that incident, in two passes: one while the draft is written, and one after it.",
    },
    { type: "heading", text: "In the draft: citations and placeholders" },
    {
      type: "paragraph",
      text: "When a draft is generated, the incident is classified and the relevant policy sections are retrieved from your department's documents. The AI writes against them. You will see two things in the narrative.",
    },
    {
      type: "bullets",
      items: [
        "**An inline citation**, in the form (Title § n - heading), such as (Riverton PD Policy Manual § 300.3 - Use of Force).",
        "**A placeholder** wherever a policy requires a detail you never gave. It reads {{[... required by Policy n]}}, so the hole is marked in the text instead of being guessed at.",
      ],
    },
    { type: "heading", text: "After the draft: the policy check" },
    {
      type: "paragraph",
      text: "Once the draft is done, the policy check reads the narrative against those sections plus a fresh look at what the narrative says. Each requirement the report leaves out, or only half covers, becomes a suggestion that starts with [Policy check · Title § n]. In the **Suggestions** tab that prefix shows as a source badge under the text.",
    },
    {
      type: "paragraph",
      text: "The two work together. The placeholder marks where the detail goes. The suggestion explains which policy wants it and what to add.",
    },
    { type: "heading", text: "Work a policy check item" },
    {
      type: "steps",
      items: [
        "Open the **Suggestions** tab and find an item with a policy badge underneath, for example \"Riverton PD Policy Manual § 300.7\".",
        "Click the badge. The policy section opens in the sidebar. Click **View full document** to see it on its page, or **Back to suggestions** to return.",
        "Fill the gap. Click the placeholder and type the real value, or click **Address Suggestions** to record or upload audio.",
        "Click **Re-evaluate**. The policy check runs again on the new text and the item clears once the report covers it.",
      ],
    },
    {
      type: "paragraph",
      text: "The check also runs again after a regeneration, so the items match the current draft. The check runs in the background, so items can appear a moment after the score updates.",
    },
    {
      type: "callout",
      tone: "tip",
      text: "A policy check item is a reading of the policy, not a finding. If the requirement does not apply to this incident, you do not have to act on it.",
    },
    {
      type: "callout",
      tone: "note",
      title: "When nothing appears",
      text: "If your department has no processed documents, none of this happens: no policy citations, no policy placeholders, and no policy check items. Very short drafts are skipped too. Reports are written the way they always were.",
    },
    {
      type: "callout",
      tone: "note",
      title: "Documents first, state law second",
      text: "When a department code section matches a violation, the narrative cites it instead of the state statute, and the Statutes panel lists it first with a Dept badge. State statutes are the fallback.",
    },
  ],
  related: ["review-the-draft", "statutes", "search-and-ask"],
};

export default lesson;
